import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link } from 'react-router-dom';
import { EmptyState } from '../../../components/layout';
import { rateBookHref } from '../../../components/SharedRecord';
import { EntryProvenanceSection } from './EntryProvenanceSection';
import { BURDEN_LABELS } from './hooks';
import { MAPPING_TYPE_LABELS } from './mappingDirection';
import { parseMappingBasis } from './mappingReview';
import { rateAssumptionNotes } from './rateApplicability';
import { compareRates } from './rateComparability';
import { rosettaConsumerContext } from './rosettaComparison';
const NO_EVIDENCE = 'No permitted rate evidence available';
const comparable = (r)=>({
        entryId: r.lcatEntryId,
        observationType: r.observationType,
        amount: r.amount,
        currency: r.currency,
        rateUnit: r.rateUnit,
        burdenBasis: r.burdenBasis,
        periodLabel: r.periodLabel,
        normalizedPeriodKey: r.normalizedPeriodKey,
        worksite: r.worksite,
        locality: r.locality
    });
const perUnit = (r)=>r.rateUnit.toLowerCase().replace('hourly', 'hr');
function LoadError({ what, message, onRetry }) {
    return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        className: "flex items-center gap-3 text-xs text-destructive",
        "data-testid": Testids.deliveryRegistries.lcatCompareError,
        children: [
            /*#__PURE__*/ _jsx("span", {
                children: message ?? `Unable to load ${what}. This is a failure, not an empty result.`
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                size: "sm",
                onClick: onRetry,
                "data-testid": Testids.deliveryRegistries.lcatCompareRetry,
                children: "Retry"
            })
        ]
    });
}
// Side by side is not a comparison: a delta (or percentage) appears only
// against the baseline (the first rate shown) and only when compareRates says
// every dimension matches. Otherwise the line says why not. No conversion, no
// blended figure, no total.
export function LcatCompareView({ mappings, mappingsQuery, rates, ratesQuery, entries, entriesQuery, candidates = [], contextEntryId = '', typeLabels, formatAmount }) {
    if (entriesQuery.isError) {
        return /*#__PURE__*/ _jsx(LoadError, {
            what: "labor categories",
            message: ConnectError.from(entriesQuery.error).code === Code.PermissionDenied ? 'You do not have access to these labor categories. Ask an administrator for registry access.' : undefined,
            onRetry: entriesQuery.refetch
        });
    }
    if (entriesQuery.isLoading) {
        return /*#__PURE__*/ _jsx("output", {
            children: "Loading labor categories…"
        });
    }
    if (mappingsQuery.isError) {
        return /*#__PURE__*/ _jsx(LoadError, {
            what: "the mapped labor categories",
            onRetry: mappingsQuery.refetch
        });
    }
    if (mappings.length === 0 && !mappingsQuery.isLoading) {
        return /*#__PURE__*/ _jsx(EmptyState, {
            title: "No approved mappings on this anchor yet",
            description: "Approve entry→anchor mappings in the Mappings queue and they line up here. A labor category with no mapping is not an error: it stays fully usable in the catalog and the Rate Book."
        });
    }
    // Use only permitted catalog definitions, never raw definition snapshots as
    // a fallback for a missing entry. The server owns endpoint applicability.
    const currentMappings = mappings.filter((m)=>{
        const entry = entries.find((e)=>e.id === m.fromEntryId);
        return !!entry && !!m.toAnchorId && !m.toEntryId && rosettaConsumerContext(entry, m).current;
    });
    const unavailable = mappings.length - currentMappings.length;
    const contextEntry = entries.find((e)=>e.id === contextEntryId);
    const ratesByEntry = new Map();
    for (const r of rates)ratesByEntry.set(r.lcatEntryId, [
        ...ratesByEntry.get(r.lcatEntryId) ?? [],
        r
    ]);
    const baseline = currentMappings.flatMap((m)=>ratesByEntry.get(m.fromEntryId) ?? [])[0];
    const withEvidence = currentMappings.filter((m)=>(ratesByEntry.get(m.fromEntryId) ?? []).length > 0);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Grouped under the same occupation for comparison. Sharing an anchor does not approve a direct relationship between these categories. A→B and B→C do not approve A→C."
            }),
            contextEntryId && /*#__PURE__*/ _jsx("output", {
                "data-testid": Testids.deliveryRegistries.lcatCompareConsumerContext,
                children: contextEntry ? `Consumer context: ${contextEntry.title} · v${contextEntry.version}. Comparison does not change this selection.` : 'Consumer definition unavailable. No selection or rate has been changed.'
            }),
            mappingsQuery.isLoading && /*#__PURE__*/ _jsx("output", {
                children: "Loading mapped definitions…"
            }),
            unavailable > 0 && /*#__PURE__*/ _jsx("output", {
                children: "Some relationships are historical, awaiting review, or lack a permitted current definition. They are excluded from current comparison. Inspect retained decisions in Mappings."
            }),
            ratesQuery.isError && /*#__PURE__*/ _jsx(LoadError, {
                what: "rates",
                onRetry: ratesQuery.refetch
            }),
            !ratesQuery.isError && !ratesQuery.isLoading && withEvidence.length < currentMappings.length && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                "data-testid": Testids.deliveryRegistries.lcatCompareCoverage,
                children: `Partial: ${withEvidence.length} of ${currentMappings.length} mapped roles have rate evidence. The rest are unknown, not zero, and nothing here is a complete estimate.`
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex gap-4 overflow-x-auto pb-2",
                children: currentMappings.map((m)=>{
                    const entry = entries.find((e)=>e.id === m.fromEntryId);
                    if (!entry) return null;
                    const context = rosettaConsumerContext(entry, m);
                    const anchor = parseMappingBasis(m.reviewedBasisJson)?.anchor;
                    const own = ratesByEntry.get(m.fromEntryId) ?? [];
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "w-72 shrink-0 rounded-md border border-border p-3 text-xs",
                        "data-testid": Testids.deliveryRegistries.lcatCompareColumn,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs font-medium uppercase tracking-wider text-muted-foreground",
                                children: entry?.companyName || 'No company (as written)'
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mt-1 font-medium",
                                children: [
                                    entry.title,
                                    " · v",
                                    entry.version
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-muted-foreground",
                                children: entry.contractVehicleName || 'No vehicle attributed'
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                "aria-label": `Definition of ${entry.title}`,
                                className: "mt-2 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "whitespace-pre-wrap",
                                        children: entry.description || 'Definition not recorded'
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Qualifications: ",
                                            entry.qualifications || 'Not recorded'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Clearance: ",
                                            entry.clearance || 'Not recorded'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Certifications: ",
                                            entry.certifications || 'Not recorded'
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                "aria-label": "Mapping explanation",
                                className: "mt-2 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            MAPPING_TYPE_LABELS[m.mappingType] ?? 'Unknown relationship',
                                            " ",
                                            m.toAnchorSocCode,
                                            ' ',
                                            m.toAnchorTitle,
                                            " · approved for v",
                                            entry.version,
                                            ". This relationship is to the occupational anchor, not to another column."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Occupational reference:",
                                            ' ',
                                            typeof anchor?.source_version === 'string' ? anchor.source_version : 'Version not recorded',
                                            ". Decision: ",
                                            m.id,
                                            " · reviewer ",
                                            m.decidedBy || 'Not recorded',
                                            "."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: `/registries?tab=lcats&mapping=${encodeURIComponent(m.id)}`,
                                        children: "Inspect reviewed evidence and decision"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(EntryProvenanceSection, {
                                entry: entry,
                                candidates: candidates
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                "aria-label": "Consumer applicability",
                                className: "mt-2 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: context.scope
                                    }),
                                    context.constraints.map((constraint)=>/*#__PURE__*/ _jsx("p", {
                                            children: constraint
                                        }, constraint)),
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "Rates are evidence with their own applicability; occupational grouping does not establish equal or interchangeable rates."
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: rateBookHref({
                                            lcat: entry.id
                                        }),
                                        children: "Open this definition in Rate Book"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mt-2 space-y-2 border-t border-border/60 pt-2",
                                children: [
                                    own.map((r)=>{
                                        const captured = Number.isFinite(r.amount) && r.amount > 0;
                                        const verdict = baseline && r.id !== baseline.id ? compareRates(comparable(baseline), comparable(r)) : null;
                                        return /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-0.5",
                                            "data-testid": Testids.deliveryRegistries.lcatCompareRate,
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center justify-between gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: "text-muted-foreground",
                                                            children: [
                                                                typeLabels[r.observationType] ?? r.observationType,
                                                                r.periodLabel ? ` · ${r.periodLabel}` : ' · no period stated',
                                                                ` · ${BURDEN_LABELS[r.burdenBasis] ?? r.burdenBasis}`,
                                                                rateAssumptionNotes(r).map((n)=>` · ${n}`)
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium",
                                                            children: captured ? `${formatAmount(r.currency, r.amount)}/${perUnit(r)}` : 'Amount not captured'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-xs text-muted-foreground",
                                                    "data-testid": Testids.deliveryRegistries.lcatCompareContext,
                                                    children: [
                                                        r.currency || 'currency not stated',
                                                        r.rateUnit.toLowerCase(),
                                                        `worksite ${[
                                                            r.worksite,
                                                            r.locality
                                                        ].filter(Boolean).join(', ') || 'not stated'}`,
                                                        `source ${r.source || 'not stated'}`
                                                    ].join(' · ')
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-xs",
                                                    "data-testid": Testids.deliveryRegistries.lcatCompareDelta,
                                                    children: verdict === null ? 'Baseline for comparison' : verdict.comparable ? `vs baseline: ${verdict.percent >= 0 ? '+' : ''}${verdict.percent.toFixed(1)}% (${verdict.delta >= 0 ? '+' : '-'}${formatAmount(r.currency, Math.abs(verdict.delta))}/${perUnit(r)})` : `not comparable: ${verdict.reasons.join('; ')}`
                                                })
                                            ]
                                        }, r.id);
                                    }),
                                    own.length === 0 && /*#__PURE__*/ _jsx("div", {
                                        className: "text-xs text-muted-foreground",
                                        "data-testid": Testids.deliveryRegistries.lcatCompareMissing,
                                        children: ratesQuery.isError ? 'Rates unavailable: loading failed' : ratesQuery.isLoading ? 'Loading rates…' : NO_EVIDENCE
                                    })
                                ]
                            })
                        ]
                    }, m.id);
                })
            })
        ]
    });
}
